// SlashCommandPopover — Polish #19.
//
// Inline autocomplete that shows up while Kimi is typing `/foo` inside the
// Composer textarea. Mirrors how Slack + Linear's slash menus work:
//
//   Type `/`            → popover opens with the full list of slash templates
//   Type `/prf`         → popover filters live to matches that start with "prf"
//   Arrow Up / Down     → move the highlighted item
//   Enter               → insert the highlighted item's resolved body
//   Escape              → dismiss the popover; `/foo` stays typed
//   Type a space        → token closes, popover dismisses (detected upstream)
//   Click an item       → same as Enter
//
// The popover is anchored to the top of the composer surface (rendered as
// an absolutely-positioned card above the textarea). Positioning-to-cursor
// would require DOM measurement plumbing that isn't worth it for a two-line
// textarea — everything Kimi would see is within the composer rectangle
// already.
//
// This component is "dumb" about textarea state: the parent owns the cursor
// position and the body text. We're only responsible for rendering the
// filtered list, tracking the keyboard-highlighted row, and firing onPick
// when a selection is made.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fillSavedReply,
  missingPlaceholders,
  type SavedReply,
  type SavedReplyContext,
} from '@/lib/savedReplies';

interface SlashCommandPopoverProps {
  /** Pre-filtered candidates for the current query. Parent manages the query. */
  matches: SavedReply[];
  /** Autofill context used to resolve placeholders at render time. */
  context: SavedReplyContext;
  /** Called when the user picks an entry (Enter or click). */
  onPick: (reply: SavedReply, resolvedBody: string) => void;
  /** Called when the user dismisses the popover (Escape, click outside). */
  onDismiss: () => void;
  /**
   * When the match list changes identity (Kimi's narrowing or widening the
   * query), we reset the keyboard-highlighted index back to 0. Use this to
   * force that reset from the parent too.
   */
  resetKey: string;
}

export function SlashCommandPopover({
  matches,
  context,
  onPick,
  onDismiss,
  resetKey,
}: SlashCommandPopoverProps) {
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Reset the highlight to the top whenever the filter changes so the first
  // visible match is always the Enter-default.
  useEffect(() => {
    setActive(0);
  }, [resetKey]);

  // Keyboard handlers are registered on window so they fire even when focus
  // is still inside the textarea. We stopPropagation on the keys we consume
  // so Cmd+Enter-to-send doesn't also fire on a pick.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (matches.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => (a + 1) % matches.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a - 1 + matches.length) % matches.length);
      } else if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
        // Only plain Enter picks. Cmd/Ctrl+Enter is the composer's "send"
        // shortcut, so let that bubble up when a slash popover happens to
        // be open (the popover dismissing is Composer's job in that case).
        e.preventDefault();
        e.stopPropagation();
        const r = matches[active];
        if (r) onPick(r, fillSavedReply(r, context));
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [matches, active, context, onPick, onDismiss]);

  // Dismiss on outside click so the popover doesn't sit around when Kimi
  // clicks elsewhere in the Center layout.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        onDismiss();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onDismiss]);

  // Precompute missing-placeholder arrays per row so we don't call the
  // helper inline twice per render.
  const rows = useMemo(
    () =>
      matches.map((r) => ({
        reply: r,
        missing: missingPlaceholders(r, context),
      })),
    [matches, context],
  );

  if (matches.length === 0) return null;

  return (
    <div ref={wrapRef} className="slash-popover" role="listbox">
      <div className="slash-popover-head">
        <span>Slash commands</span>
        <span className="slash-popover-hint">
          <kbd className="kbd-inline">↑↓</kbd>
          <kbd className="kbd-inline">⏎</kbd>
          to pick
          <span aria-hidden>·</span>
          <kbd className="kbd-inline">esc</kbd>
          to close
        </span>
      </div>
      {rows.map(({ reply, missing }, idx) => {
        const isActive = idx === active;
        return (
          <button
            key={reply.id}
            type="button"
            role="option"
            aria-selected={isActive}
            className={`slash-popover-item${isActive ? ' is-active' : ''}`}
            // Avoid stealing focus from the textarea on click.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(idx)}
            onClick={() => {
              onPick(reply, fillSavedReply(reply, context));
            }}
          >
            <span className="slash-popover-cmd">/{reply.slash}</span>
            <span className="slash-popover-label">{reply.label}</span>
            {missing.length > 0 ? (
              <span className="slash-popover-missing">needs {missing.join(', ')}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
